import { Dispatch, SetStateAction, useMemo, useRef } from 'react';

import {
  ComparisonViewColumn,
  ExperimentViewOverridesSchema,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  ATTACHMENTS_COLUMN_IDX,
  DatasetSessionCompareSettings,
  INPUT_COLUMN_IDX,
  METRICS_COLUMNS,
  OUTPUT_START_COLUMN_IDX,
  REFERENCE_COLUMN_IDX,
} from '../../constants';
import {
  getColumnDisplayName,
  getColumnShortName,
} from '../../utils/columnTypeUtils';
import {
  type ColumnRenderFunctions,
  type ExperimentViewMetric,
  renderOverriddenColumns,
} from '../../utils/renderOverriddenColumns';
import { ExperimentColumnHeader } from '../SingleExperimentColumns/ExperimentColumnHeader';
import { FeedbackColumnHeader } from '../SingleExperimentColumns/FeedbackColumns/FeedbackColumnHeaders';
import { MetricsColumnHeader } from '../SingleExperimentColumns/MetricsColumns/MetricsColumnHeaders';
import { useSessionExtras } from '../hooks/useSessionExtras';
import { useGroupByMetadata } from './useGroupByMetadata';
import { useSetTableFullWidth } from './useTableFullWidth';

export const ExperimentGroupByHeader = ({
  displaySettingsState,
  columnWidthStorage,
  setColumnWidthStorage,

  sessionIds,
  sessionExtras,

  columnsSorted,
  tableHeight,
  datasetExperimentView,
}: {
  displaySettingsState: DatasetSessionCompareSettings;
  columnWidthStorage: number[];
  setColumnWidthStorage: Dispatch<SetStateAction<number[]>>;

  sessionIds: string[];
  sessionExtras: ReturnType<typeof useSessionExtras>;

  columnsSorted: ComparisonViewColumn[];

  tableHeight: number;
  datasetExperimentView?: ExperimentViewOverridesSchema;
}) => {
  const { metadataKey: groupByMetadataKey } = useGroupByMetadata();

  const { aggregateFeedbackKeys, feedbackRunFacets, metricsRunFacets } =
    sessionExtras;

  const {
    isReferenceInputHidden,
    isAttachmentsHidden,
    isReferenceOutputHidden,
    hiddenMetricsColumns,
    hiddenFeedbackColumns,
  } = displaySettingsState;

  const ref = useRef<HTMLDivElement>(null);
  // Use this header ref to set the full width of the table.
  useSetTableFullWidth(columnWidthStorage, ref);

  const displayedMetrics = Object.values(METRICS_COLUMNS).filter(
    (metric) => !hiddenMetricsColumns.includes(metric.name)
  );

  // Helper function to render individual feedback header
  const renderFeedbackHeader = (feedbackKey: string, columnIndex: number) => (
    <FeedbackColumnHeader
      key={`header-feedback-${feedbackKey}-${columnIndex}`}
      feedbackKey={feedbackKey}
      columnIndex={columnIndex}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      runFacets={feedbackRunFacets}
      currentFilters={[]}
      className={cn(groupByMetadataKey && 'bg-background')}
      canSort={!groupByMetadataKey}
      isTable={false}
    />
  );

  // Helper function to render individual metrics header
  const renderMetricsHeader = (
    metric: ExperimentViewMetric,
    columnIndex: number
  ) => (
    <MetricsColumnHeader
      key={`header-metrics-${metric.name}-${columnIndex}`}
      metric={metric}
      columnIndex={columnIndex}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      runFacets={metricsRunFacets}
      currentFilters={[]}
      className={cn(groupByMetadataKey && 'bg-background', 'h-[64px]')}
      isTable={false}
    />
  );

  // Define header components
  const renderInputsHeader = (columnName: string, columnIndex: number) => {
    const inputHeader = (
      <ExperimentColumnHeader
        columnIndex={columnIndex}
        columnName={columnName}
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        className={cn(
          sessionIds.length === 1 && 'h-[64px]',
          groupByMetadataKey && 'bg-background'
        )}
        isTable={false}
      >
        <div className="h-full">Inputs</div>
      </ExperimentColumnHeader>
    );
    return inputHeader;
  };

  const renderAttachmentsHeader = (columnIndex: number) => {
    const attachmentsHeader = (
      <ExperimentColumnHeader
        columnIndex={columnIndex}
        columnName="Attachments"
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        className={cn(
          sessionIds.length === 1 && 'h-[64px]',
          groupByMetadataKey && 'bg-background'
        )}
        isTable={false}
      >
        <div className="h-full">Attachments</div>
      </ExperimentColumnHeader>
    );
    return attachmentsHeader;
  };

  const renderReferenceOutputsHeader = (
    columnName: string,
    columnIndex: number
  ) => {
    const referenceOutputHeader = (
      <ExperimentColumnHeader
        columnIndex={columnIndex}
        columnName={columnName}
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        className={cn(
          sessionIds.length === 1 && 'h-[64px]',
          groupByMetadataKey && 'bg-background'
        )}
        isTable={false}
      >
        <div className="h-full">Reference Output</div>
      </ExperimentColumnHeader>
    );
    return referenceOutputHeader;
  };

  const renderDefaultOutputsHeader = (columnStartIndex: number) => {
    const outputsHeader = (
      <>
        {columnsSorted.map((_, idx) => {
          return (
            <ExperimentColumnHeader
              key={idx}
              columnIndex={idx + columnStartIndex}
              columnName="Output"
              tableHeight={tableHeight}
              columnWidthStorage={columnWidthStorage}
              setColumnWidthStorage={setColumnWidthStorage}
              className={cn('h-[64px]', groupByMetadataKey && 'bg-background')}
              isTable={false}
            >
              <div className="flex h-full flex-col">Output</div>
            </ExperimentColumnHeader>
          );
        })}
      </>
    );
    return outputsHeader;
  };

  const renderComputedColumnHeader = (
    columnName: string,
    columnIndex: number
  ) => {
    const displayName = getColumnShortName(columnName);
    return (
      <ExperimentColumnHeader
        key={`header-${columnName}-${columnIndex}`}
        columnIndex={columnIndex}
        columnName={columnName}
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        className={cn(
          sessionIds.length === 1 && 'h-[64px]',
          groupByMetadataKey && 'bg-background'
        )}
        isTable={false}
      >
        <div className="flex h-full flex-col items-start gap-1">
          <div>{displayName}</div>
          <div className="normal-case text-quaternary">
            {getColumnDisplayName(columnName)}
          </div>
        </div>
      </ExperimentColumnHeader>
    );
  };

  const feedbacksHeader = (
    <>
      {aggregateFeedbackKeys
        .filter((key) => !hiddenFeedbackColumns.includes(key))
        .map((key, i) =>
          renderFeedbackHeader(key, OUTPUT_START_COLUMN_IDX + i + 1)
        )}
    </>
  );

  const metricsHeader = displayedMetrics.map((metric, i) =>
    renderMetricsHeader(
      metric,
      OUTPUT_START_COLUMN_IDX +
        aggregateFeedbackKeys.filter(
          (key) => !hiddenFeedbackColumns.includes(key)
        ).length +
        1 +
        i
    )
  );

  const defaultHeader = (
    <>
      {!isReferenceInputHidden &&
        renderInputsHeader('Inputs', INPUT_COLUMN_IDX)}
      {!isAttachmentsHidden && renderAttachmentsHeader(ATTACHMENTS_COLUMN_IDX)}
      {!isReferenceOutputHidden &&
        renderReferenceOutputsHeader('Reference Output', REFERENCE_COLUMN_IDX)}
      {renderDefaultOutputsHeader(OUTPUT_START_COLUMN_IDX)}
      {feedbacksHeader}
      {metricsHeader}
    </>
  );

  // Generate headers based on configuration
  let configuredHeaders: React.ReactNode[] = [];
  if (datasetExperimentView?.column_overrides && sessionIds.length === 1) {
    const renderFunctions: ColumnRenderFunctions = {
      renderInputsColumn: renderInputsHeader,
      renderAttachmentsColumn: renderAttachmentsHeader,
      renderReferenceOutputsColumn: renderReferenceOutputsHeader,
      renderOutputsColumn: renderDefaultOutputsHeader,
      renderFeedbackColumn: renderFeedbackHeader,
      renderMetricsColumn: renderMetricsHeader,
      renderComputedColumn: renderComputedColumnHeader,
    };

    configuredHeaders = renderOverriddenColumns(
      datasetExperimentView.column_overrides,
      {
        isReferenceInputHidden,
        isAttachmentsHidden,
        isReferenceOutputHidden,
        hiddenFeedbackColumns,
        hiddenMetricsColumns,
      },
      renderFunctions,
      aggregateFeedbackKeys,
      sessionIds.length === 1
    );
  }

  // Final header - either configured or default
  const finalHeader = useMemo(() => {
    if (configuredHeaders.length > 0) {
      return configuredHeaders;
    }
    return defaultHeader;
  }, [configuredHeaders, defaultHeader]);

  return (
    <div className={cn('flex bg-background')} ref={ref}>
      {finalHeader}
      <div className="w-full border-t border-secondary"></div>
    </div>
  );
};
